import type { FlowDocument } from '@miso/design-system';
import type { StartVisitCounselDepartureAddress } from '@/entities/visit-counsel/model/visit-counsel.types';

const Q_ADDRESS = 'address';
const Q_BUILDING = 'buildingName';
const Q_DONG = 'dong';
const Q_HO = 'unit';
const Q_FLOOR = 'floor';

const STEP_DETAILS = 'details';
const TERMINAL = 'saved';

export const ARRIVAL_ADDRESS_QUESTIONS = {
  Q_ADDRESS,
  Q_BUILDING,
  Q_DONG,
  Q_HO,
  Q_FLOOR,
} as const;

const NONE_DONG = '__none_dong__';

export const ARRIVAL_NONE_SENTINELS = { dong: NONE_DONG } as const;

/**
 * 도착지 주소 InquiryFlow (Figma `222:13604` ~ `222:13669`).
 *
 * 단일 step(STEP_DETAILS) + L2 cascade — 도로명 주소 → 건물명 → 동 → 호(/층)가
 * 한 화면에서 누적되며 step 전환 CTA 가 발생하지 않는다. address question 은
 * BottomSheet 트리거 인풋이라 cascade 안에서도 자연스럽게 작동한다.
 *
 * 분기 처리:
 * - Q_DONG 의 escape("동 없음") → sentinel (`__none_dong__`)
 * - Q_HO / Q_FLOOR 는 `switchTo` 로 **슬롯 전환** 구현:
 *   - Q_HO 에 "호 수 없음" 칩 → 같은 슬롯에서 Q_FLOOR 로 교체 표시
 *   - Q_FLOOR 에 "호 수 있음" 칩 → Q_HO 로 복원
 *   - 전환 시 이전 슬롯 값 자동 reset (useInquiryFlow.switchVariant 동작)
 *   - Q_FLOOR 는 variant이므로 cascade 에 별도 question 으로 추가되지 않음
 *
 * 백엔드 매핑(`flowResultToArrivalAddress`):
 *   - values[Q_FLOOR] 비어있지 않음 → floor = Q_FLOOR, unit = null
 *   - values[Q_HO] 비어있지 않음   → unit = Q_HO, floor = null
 */
export function buildArrivalAddressFlow(): FlowDocument {
  return {
    schemaVersion: 1,
    id: 'progress-arrival-address',
    initial: STEP_DETAILS,
    meta: { appBarTitle: '도착지 주소' },
    steps: {
      [STEP_DETAILS]: {
        kind: 'questions',
        questions: [
          {
            id: Q_ADDRESS,
            layout: 'L2',
            title: '이사 가시는 집은 어디인가요?',
            ctaLabel: '다음',
            input: { kind: 'address', required: true, placeholder: '도착지 주소 검색' },
          },
          {
            id: Q_BUILDING,
            layout: 'L2',
            title: '건물 이름을 알려주세요.',
            ctaLabel: '다음',
            initialValueFrom: `${Q_ADDRESS}.buildingName`,
            input: {
              kind: 'text',
              required: false,
              placeholder: '건물 이름',
              maxLength: 30,
            },
          },
          {
            id: Q_DONG,
            layout: 'L2',
            title: '동을 알려주세요.',
            ctaLabel: '다음',
            input: {
              kind: 'suffix',
              required: false,
              suffix: '동',
              inputType: 'text',
              placeholder: '동',
              escape: { label: '동 없음', value: NONE_DONG },
            },
          },
          {
            id: Q_HO,
            layout: 'L2',
            title: '호 수를 알려주세요.',
            ctaLabel: '다음',
            // "호 수 없음" 클릭 시 같은 슬롯에서 Q_FLOOR 질문으로 교체 (cascade 에
            // 별도 질문이 추가되는 것이 아님 — switchTo variant 메커니즘).
            switchTo: { questionId: Q_FLOOR, label: '호 수 없음' },
            input: {
              kind: 'suffix',
              required: true,
              suffix: '호',
              inputType: 'text',
              placeholder: '호',
            },
          },
          {
            id: Q_FLOOR,
            layout: 'L2',
            title: '층 수를 알려주세요.',
            ctaLabel: '다음',
            // Q_HO 의 switchTo variant — cascade 에 직접 추가되지 않고 Q_HO 슬롯을 점유.
            // "호 수 있음" 클릭 시 Q_HO 로 복원.
            switchTo: { questionId: Q_HO, label: '호 수 있음' },
            input: {
              kind: 'suffix',
              required: true,
              suffix: '층',
              inputType: 'number',
              placeholder: '0',
              min: 0,
              step: 1,
            },
          },
        ],
        next: { kind: 'terminal', target: TERMINAL },
      },
    },
    terminals: { [TERMINAL]: { kind: 'placeholder-auto' } },
  };
}

// ── Result mapping ───────────────────────────────────────────────────────────

/**
 * InquiryFlow 답변 → StartVisitCounselDepartureAddress (Request body 호환 형식).
 *
 * - 도로명 주소: Q_ADDRESS.roadAddress (필수, 미입력이면 null 반환)
 * - 건물명: Q_BUILDING (또는 Q_ADDRESS.buildingName)
 * - 동: Q_DONG !== sentinel 이면 그 값, 아니면 null
 * - 호/층: Q_HO !== sentinel 이면 unit = Q_HO, floor = null. 아니면 floor = Q_FLOOR.
 */
export function flowResultToArrivalAddress(
  values: Record<string, unknown>
): StartVisitCounselDepartureAddress | null {
  const addr = values[Q_ADDRESS] as
    | { roadAddress?: string; buildingName?: string }
    | null
    | undefined;
  if (!addr || typeof addr.roadAddress !== 'string' || addr.roadAddress.trim() === '') {
    return null;
  }
  const buildingNameRaw = values[Q_BUILDING];
  const buildingName =
    typeof buildingNameRaw === 'string' && buildingNameRaw.trim() !== ''
      ? buildingNameRaw.trim()
      : typeof addr.buildingName === 'string' && addr.buildingName.trim() !== ''
        ? addr.buildingName.trim()
        : null;
  const dongRaw = values[Q_DONG];
  const dong =
    typeof dongRaw === 'string' && dongRaw !== '' && dongRaw !== NONE_DONG ? dongRaw : null;

  // switchTo 메커니즘: 활성 슬롯(Q_HO or Q_FLOOR)에만 값이 채워지고 반대편은 reset.
  // → 비어있지 않은 쪽을 우선 사용해 unit/floor 판단.
  const hoRaw = values[Q_HO];
  const floorRaw = values[Q_FLOOR];
  let unit: string | null;
  let floor: string | null;
  if (typeof floorRaw === 'string' && floorRaw !== '') {
    unit = null;
    floor = floorRaw;
  } else if (typeof hoRaw === 'string' && hoRaw !== '') {
    unit = hoRaw;
    floor = null;
  } else {
    unit = null;
    floor = null;
  }

  return {
    roadAddress: addr.roadAddress,
    buildingName,
    dong,
    floor,
    unit,
  };
}

/**
 * StructuredDepartureAddress → InquiryFlow initialValues.
 *
 * 구현은 `@miso/counsel-schema` 에 있다 — 프리뷰도 같은 계산을 써야 "이미 입력된 채로
 * 열리는가" 를 프리뷰에서 확인할 수 있다.
 */
export { arrivalAddressToFlowValues } from '@miso/counsel-schema';
